// jsPDF, autoTable, ExcelJS loaded dynamically to reduce initial bundle (~1.3MB total)
// Pattern matches existing dynamic imports in SpendManagementNav, InvoiceNavbar, SchedulingProfileNav
import { saveAs } from "file-saver"
import constants from "./en"
import {
  drawConfidentialFooter,
  drawConfidentialWatermark,
} from "./commonUtills"

// Import logos so webpack resolves the correct hashed filenames.
// hayesDarkBandSrc is the white-on-transparent stacked variant — visible on
// the slate PDF header band and on the navy XLSX header rows.
import locumscopeSrc from "../assets/images/logo-nav.png"
import hayesDarkBandSrc from "../assets/images/Logos/Hayes-Locums_stacked_RGB_white.png"
// Note: HayesIcon.svg removed — jsPDF does not support SVG format.
// The text "CONFIDENTIAL" watermark (already implemented) replaces it.

/**
 * Strip the data URL prefix (e.g. "data:image/png;base64,") from a data URL string.
 * ExcelJS addImage requires a raw base64 string — not a full data URL.
 * Returns the string unchanged if it does not contain a comma (already raw).
 *
 * @param {string} dataUrl
 * @returns {string}
 */
function stripDataUrl(dataUrl) {
  if (typeof dataUrl === "string" && dataUrl.includes(",")) {
    return dataUrl.split(",")[1]
  }
  return dataUrl
}

/**
 * Fetch an image URL and return a base64 data-URL string.
 * Returns null if fetch fails (logos are optional — export continues without them).
 */
async function toBase64(src) {
  try {
    const res = await fetch(src)
    const blob = await res.blob()
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.readAsDataURL(blob)
    })
  } catch {
    // best-effort: image fetch failed, caller renders without it
    return null
  }
}

/**
 * Build a human-readable filter summary string from active chip filters.
 *
 * @param {Object} chipFilters — e.g. { status: ['Active'], userType: ['Admin'] }
 * @param {Array}  [chipConfigs] — e.g. [{ key: 'status', label: 'Status' }, ...]
 * @param {string} [searchTerm] — global search input value
 * @returns {string} e.g. "Status: Active | User Type: Admin | Search: 'mercy'"
 */
export function buildFilterSummary(
  chipFilters = {},
  chipConfigs = [],
  searchTerm = "",
) {
  const parts = []

  for (const { key, label } of chipConfigs) {
    const values = chipFilters[key]
    if (Array.isArray(values) && values.length > 0) {
      parts.push(`${label}: ${values.join(", ")}`)
    } else if (typeof values === "string" && values) {
      parts.push(`${label}: ${values}`)
    }
  }

  if (searchTerm?.trim()) {
    parts.push(`Search: "${searchTerm.trim()}"`)
  }

  return parts.join(" | ")
}

/**
 * Compute the Y position for the large-row-count notice on the first page.
 * Both filterSummary and truncated warnings occupy a line above this notice.
 *
 * @param {string}  filterSummary — non-empty string when filters are active
 * @param {boolean} truncated     — true when columns were capped at 12
 * @returns {number} Y coordinate in mm
 */
function getContentStartY(filterSummary, truncated) {
  if (filterSummary && truncated) return 39
  if (filterSummary) return 35
  if (truncated) return 35
  return 30
}

/**
 * Draw the title, timestamp, optional filter summary, optional truncation
 * warning, and optional large-row-count notice on the first page of the PDF.
 * Called only when data.pageNumber === 1.
 *
 * @param {Object} doc             — jsPDF instance
 * @param {Object} opts
 * @param {string} opts.title
 * @param {string} opts.timestamp
 * @param {string} opts.filterSummary
 * @param {boolean} opts.truncated
 * @param {number} opts.colCount   — original (pre-truncation) column count
 * @param {Array}  opts.rows       — original (pre-truncation) row array
 * @param {number} opts.pageW      — page width in mm
 */
function drawFirstPageMetadata(
  doc,
  { title, timestamp, filterSummary, truncated, colCount, rows, pageW },
) {
  doc.setFontSize(14)
  doc.setFont("helvetica", "bold")
  doc.setTextColor("#334155")
  doc.text(title, 10, 24)

  doc.setFontSize(8)
  doc.setFont("helvetica", "normal")
  doc.setTextColor("#64748b")
  doc.text(`Generated: ${timestamp}`, pageW - 10, 24, { align: "right" })

  if (filterSummary) {
    doc.setFontSize(9)
    doc.text(`Filters: ${filterSummary}`, 10, 30)
  }

  if (truncated) {
    doc.setFontSize(7)
    doc.setTextColor("#dc2626")
    const omitted = colCount - 12
    doc.text(
      `* ${omitted} column${omitted > 1 ? "s" : ""} omitted due to page width`,
      10,
      filterSummary ? 35 : 30,
    )
  }

  if (rows.length > 500) {
    const yPos = getContentStartY(filterSummary, truncated)
    doc.setFontSize(7)
    doc.setTextColor("#64748b")
    doc.text(
      `Showing ${rows.length.toLocaleString()} rows — apply filters to reduce export size`,
      10,
      yPos,
    )
  }
}

/**
 * Export tabular data as a branded PDF with watermark and confidentiality notice.
 *
 * @param {Object}     options
 * @param {string}     options.title          — Report title (e.g. "Facility — Users")
 * @param {string}     options.filename       — Output filename without extension
 * @param {string[]}   options.headers        — Column header labels
 * @param {string[][]} options.rows           — 2D array of cell values (already filtered)
 * @param {string}     [options.filterSummary] — Human-readable filter description
 * @param {string}     [options.orientation]   — "portrait" | "landscape" (default: auto based on column count)
 */
export async function exportTableToPdf({
  title,
  filename,
  headers,
  rows,
  filterSummary = "",
  orientation,
}) {
  const colCount = headers.length

  // Always landscape when not explicitly set — portrait truncates typical table widths
  if (!orientation) {
    orientation = "landscape"
  }

  // Large table guard: truncate to 12 columns max
  let truncated = false
  let displayHeaders = headers
  let displayRows = rows
  if (colCount > 12) {
    truncated = true
    displayHeaders = headers.slice(0, 12)
    displayRows = rows.map((r) => r.slice(0, 12))
  }

  const { default: jsPDF } = await import("jspdf")
  const { default: autoTable } = await import("jspdf-autotable")
  // compress: true enables PDF stream deflation (typically 30–60% smaller).
  const doc = new jsPDF({
    orientation,
    unit: "mm",
    format: "a4",
    compress: true,
  })
  const pageW = doc.internal.pageSize.getWidth()

  // Pre-load logos in parallel (SVG icon removed — jsPDF does not support SVG)
  const [locumscopeLogo, hayesLogo] = await Promise.all([
    toBase64(locumscopeSrc),
    toBase64(hayesDarkBandSrc),
  ])

  const timestamp = new Date().toLocaleString()

  const drawPage = (data) => {
    const pageH = doc.internal.pageSize.getHeight()

    // ── Header band — full width, 18mm height, slate ──
    doc.setFillColor(51, 65, 85) // #334155
    doc.rect(0, 0, pageW, 18, "F")
    doc.setFontSize(7)
    doc.setFont("helvetica", "bold")
    doc.setTextColor(255, 255, 255)
    doc.text("CONFIDENTIAL", pageW / 2, 11, { align: "center" })

    // ── Logos on every page ── sized to preserve native aspect ratios
    // (locumscope ~2.8:1, hayes ~6:1) so text doesn't appear squished.
    // alias dedupes the PNG bytes across pages.
    if (locumscopeLogo)
      doc.addImage(locumscopeLogo, "PNG", 10, 4, 28, 10, "ls-logo")
    if (hayesLogo)
      doc.addImage(hayesLogo, "PNG", pageW - 39, 4, 29, 10, "hl-logo")

    drawConfidentialWatermark(doc, { fontSize: 48 })

    // ── Title + metadata on first page only ──
    if (data.pageNumber === 1) {
      drawFirstPageMetadata(doc, {
        title,
        timestamp,
        filterSummary,
        truncated,
        colCount,
        rows,
        pageW,
      })
    }

    // ── Footer: page number + confidentiality ──
    const totalPages = doc.internal.getNumberOfPages()
    doc.setFontSize(7)
    doc.setTextColor(100)
    doc.text(
      `Page ${data.pageNumber} of ${totalPages}`,
      pageW / 2,
      pageH - 14,
      { align: "center" },
    )
    drawConfidentialFooter(doc, { fontSize: 6, y: pageH - 8 })
  }

  // Calculate startY based on metadata rows
  let startY = 29
  if (filterSummary) startY = 34
  if (truncated) startY += 4
  if (rows.length > 500) startY += 4

  autoTable(doc, {
    head: [displayHeaders],
    body: displayRows,
    startY,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: "#334155", textColor: "#fff", fontStyle: "bold" },
    alternateRowStyles: { fillColor: "#F8FAFC" },
    didDrawPage: drawPage,
    margin: { top: 22, left: 10, right: 10 },
  })

  doc.save(`${filename}.pdf`)
}

/**
 * Export tabular data as a branded XLSX with watermark, logos, and sheet protection.
 *
 * @param {Object}     options
 * @param {string}     options.title          — Report title
 * @param {string}     options.filename       — Output filename without extension
 * @param {string[]}   options.headers        — Column header labels
 * @param {string[][]} options.rows           — 2D array of cell values (already filtered)
 * @param {string}     [options.filterSummary] — Human-readable filter description
 */
export async function exportTableToExcel({
  title,
  filename,
  headers,
  rows,
  filterSummary = "",
}) {
  const { default: ExcelJS } = await import("exceljs")
  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet("Sheet1")

  // Default-unlock every cell via column style — must be set before any cell
  // is written so locked:false actually applies. Banner cell overrides back
  // to locked:true so sheet protection guards only the confidentiality notice.
  worksheet.columns = headers.map(() => ({
    width: 20,
    style: { protection: { locked: false } },
  }))

  // ── Logos ──
  const [locumscopeB64, hayesB64] = await Promise.all([
    toBase64(locumscopeSrc),
    toBase64(hayesDarkBandSrc),
  ])

  // Rows 1-3: Logo header (merged)
  worksheet.mergeCells("A1:D3")
  if (headers.length > 4) {
    const lastCol = String.fromCodePoint(64 + Math.min(headers.length, 26))
    worksheet.mergeCells(`E1:${lastCol}3`)
  }

  // Navy fill behind the logos — matches the --hero-bg-start brand token.
  // Logo cells are locked so the brand header can't be edited or deleted.
  const logoFill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF1E3A5F" },
  }
  const logoAnchorA = worksheet.getRow(1).getCell(1)
  logoAnchorA.fill = logoFill
  logoAnchorA.protection = { locked: true }
  if (headers.length > 4) {
    const logoAnchorB = worksheet.getRow(1).getCell(5)
    logoAnchorB.fill = logoFill
    logoAnchorB.protection = { locked: true }
  }

  // Use tl + ext (not br) so logos render at their native aspect ratio
  // instead of being stretched to fit the merged cell range.
  if (locumscopeB64) {
    const logoId = workbook.addImage({
      base64: stripDataUrl(locumscopeB64),
      extension: "png",
    })
    worksheet.addImage(logoId, {
      tl: { col: 0, row: 0 },
      ext: { width: 140, height: 50 },
      editAs: "oneCell",
    })
  }
  if (hayesB64) {
    const hayesId = workbook.addImage({
      base64: stripDataUrl(hayesB64),
      extension: "png",
    })
    worksheet.addImage(hayesId, {
      // headers.length - 1.04 = right edge of logo flush with right edge of
      // the last data column (145px logo / ~140px col-20 width ≈ 1.04 cols).
      tl: { col: Math.max(4, headers.length - 1.04), row: 0 },
      ext: { width: 145, height: 50 },
      editAs: "oneCell",
    })
  }

  // Lock the chrome rows (title, timestamp, filter, header) across the full
  // data width so the report identity can't be tampered with.
  const lockChromeRow = (row) => {
    for (let c = 1; c <= headers.length; c++) {
      row.getCell(c).protection = { locked: true }
    }
  }

  // Row 4: Title
  let dataStartRow = 4
  const titleRow = worksheet.getRow(dataStartRow)
  titleRow.getCell(1).value = title
  titleRow.getCell(1).font = {
    bold: true,
    size: 14,
    color: { argb: "FF334155" },
  }
  lockChromeRow(titleRow)
  dataStartRow++

  // Row 5: Timestamp
  const tsRow = worksheet.getRow(dataStartRow)
  tsRow.getCell(1).value = `Generated: ${new Date().toLocaleString()}`
  tsRow.font = { italic: true, size: 9, color: { argb: "FF64748B" } }
  lockChromeRow(tsRow)
  dataStartRow++

  // Row 6: Filter summary (if any)
  if (filterSummary) {
    const filterRow = worksheet.getRow(dataStartRow)
    filterRow.getCell(1).value = `Filters: ${filterSummary}`
    filterRow.font = { italic: true, size: 9, color: { argb: "FF64748B" } }
    lockChromeRow(filterRow)
    dataStartRow++
  }

  // Confidentiality banner — merged across data columns, frozen with the
  // column header below it so the notice stays visible while scrolling.
  const lastColLetter = String.fromCodePoint(64 + Math.min(headers.length, 26))
  worksheet.mergeCells(`A${dataStartRow}:${lastColLetter}${dataStartRow}`)
  const bannerRow = worksheet.getRow(dataStartRow)
  const bannerCell = bannerRow.getCell(1)
  bannerCell.value = constants.WATERMARK_FOOTER_TEXT
  bannerCell.font = { bold: true, size: 10, color: { argb: "FF991B1B" } }
  bannerCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFFEE2E2" },
  }
  bannerCell.alignment = {
    horizontal: "center",
    vertical: "middle",
    wrapText: true,
  }
  bannerCell.protection = { locked: true }
  bannerRow.height = 32
  dataStartRow++

  // Column headers (bold, green fill)
  const headerRowNumber = dataStartRow
  const headerRow = worksheet.getRow(headerRowNumber)
  headers.forEach((col, i) => {
    const cell = headerRow.getCell(i + 1)
    cell.value = col
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } }
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF334155" },
    }
    cell.alignment = { horizontal: "center" }
  })
  headerRow.height = 18

  // Data rows
  for (const row of rows) {
    worksheet.addRow(row)
  }

  // Freeze the banner + column header so they stay visible while scrolling.
  worksheet.views = [{ state: "frozen", ySplit: headerRowNumber }]

  // Print-only watermark via header/footer (text-only — ExcelJS doesn't
  // support header/footer images). Invisible while editing; appears in
  // Print Preview / printed / PDF output.
  worksheet.headerFooter.oddHeader = `&C&K808080&"Arial,Bold"24${constants.WATERMARK_TEXT}`
  worksheet.headerFooter.oddFooter = constants.WATERMARK_FOOTER_TEXT

  // Lock the banner + chrome rows + logo drawings (objects: false → OOXML
  // objects="1", which protects images from deletion). Permissive flags
  // still let users sort/filter/edit data cells.
  await worksheet.protect("", {
    selectLockedCells: true,
    selectUnlockedCells: true,
    formatCells: true,
    formatColumns: true,
    formatRows: true,
    insertRows: true,
    deleteRows: true,
    sort: true,
    autoFilter: true,
    objects: false,
  })

  const buffer = await workbook.xlsx.writeBuffer()
  saveAs(new Blob([buffer]), `${filename}.xlsx`)
}
